import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ContactForm from './ContactForm';
import { getLogoBrandParts } from '../Utils/brand';
import { getAuthConfig } from '../Utils/authConfig';
import { UserCircleIcon, UserIcon } from './Icons';
import { isTelegramMiniApp } from '../Utils/miniApp';

export default function Navbar() {
  const logoParts = getLogoBrandParts();
  const [open, setOpen] = useState(false);  
  const [showContactForm, setShowContactForm] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [isMiniApp, setIsMiniApp] = useState(false);
  const [authConfig, setAuthConfig] = useState({ authEnabled: false, botUsername: '' });
  const [currentUser, setCurrentUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const syncUserFromStorage = useCallback(() => {
    try {
      const rawUser = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
      const token = localStorage.getItem('token') || localStorage.getItem('adminToken');
      if (rawUser && token) {
        setCurrentUser(JSON.parse(rawUser));
      } else {
        setCurrentUser(null);
      }
    } catch (e) {
      setCurrentUser(null);
    }
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('telegram_user');
    localStorage.removeItem('telegramUser');
    localStorage.removeItem('token');
    localStorage.removeItem('adminToken');
    setCurrentUser(null);
    window.dispatchEvent(new Event('domainer_auth_change'));
    window.dispatchEvent(new Event('authChange'));
    if (location.pathname.startsWith('/cabinet') || location.pathname.startsWith('/admin')) {
      navigate('/');
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    syncUserFromStorage();
    window.addEventListener('storage', syncUserFromStorage);
    window.addEventListener('domainer_auth_change', syncUserFromStorage);
    window.addEventListener('authChange', syncUserFromStorage);
    return () => {
      window.removeEventListener('storage', syncUserFromStorage);
      window.removeEventListener('domainer_auth_change', syncUserFromStorage);
      window.removeEventListener('authChange', syncUserFromStorage);
    };
  }, [syncUserFromStorage, location.pathname]);

  // Robust click outside listener for burger dropdown
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        triggerRef.current && 
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsMiniApp(isTelegramMiniApp());
    }

    getAuthConfig()
      .then(data => {
        if (data && (data.success || data.authEnabled !== undefined)) {
          setAuthConfig(data);
          if (data.authEnabled === false) {
            const adminToken = localStorage.getItem('adminToken');
            const rawUser = localStorage.getItem('telegram_user') || localStorage.getItem('telegramUser');
            let parsed = null;
            try { parsed = JSON.parse(rawUser); } catch (_) {}
            const isAdmin = parsed?.role === 'admin' || Boolean(adminToken);
            if (!isAdmin && rawUser) {
              localStorage.removeItem('telegram_user');
              localStorage.removeItem('telegramUser');
              localStorage.removeItem('token');
              setCurrentUser(null);
              window.dispatchEvent(new Event('domainer_auth_change'));
              window.dispatchEvent(new Event('authChange'));
            }
          }
        }
      })
      .catch(() => {});
  }, []);

  const [isScrolled, setIsScrolled] = useState(false);
  const isScrolledRef = useRef(false);

  // Track scroll position to blend seamlessly at top and elevate with glass + bottom line on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 12;
      if (scrolled !== isScrolledRef.current) {
        isScrolledRef.current = scrolled;
        setIsScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setAnimKey(prev => prev + 1);

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header className={`${
        isMiniApp 
          ? 'relative w-full z-50 bg-[#090a0f]/95 border-b border-white/[0.08]' 
          : `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
              isScrolled
                ? 'bg-[var(--theme-bg-base,#050507)]/80 backdrop-blur-xl shadow-[0_12px_36px_rgba(0,0,0,0.65)] py-3'
                : 'bg-transparent backdrop-blur-none py-4'
            }`
      } px-4 sm:px-6 md:px-8 transition-all duration-300`}>
        {/* Top edge subtle specular highlight - only on scroll */}
        <div className={`absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none transition-opacity duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`} />

        {/* Bottom dividing line: only visible on scroll detachment */}
        <div className={`absolute inset-x-0 bottom-0 h-[1px] transition-all duration-300 pointer-events-none ${
          isScrolled 
            ? 'opacity-100 bg-gradient-to-r from-transparent via-white/[0.12] to-transparent shadow-[0_1px_8px_rgba(0,0,0,0.5)]' 
            : 'opacity-0 bg-transparent'
        }`} />
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-2">
            <Link 
              to="/" 
              onClick={handleLogoClick} 
              className="group flex items-center space-x-2 cursor-pointer select-none"
            >
              <h1 className="text-xl font-display font-bold text-white flex items-center gap-2 cursor-pointer select-none group">
                <motion.span
                  key={`logo-text-${animKey}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  whileHover={{ 
                    scale: 1.04,
                    color: "#93c5fd",
                    textShadow: "0 0 16px rgba(59, 130, 246, 0.5)"
                  }}
                  className="text-white transition-colors duration-200 tracking-tight inline-block"
                >
                  {logoParts.text}
                </motion.span>
                <motion.span 
                  key={`logo-badge-${animKey}`}
                  initial={{ x: -28, rotate: -180, scale: 0.2, opacity: 0 }}
                  animate={{ 
                    x: 0, 
                    rotate: 0, 
                    scale: 1, 
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 280,
                      damping: 18,
                      mass: 0.8,
                      delay: 0.2
                    }
                  }}
                  whileHover={{ 
                    scale: 1.12, 
                    rotate: [0, -6, 6, 0],
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.92 }}
                  className="w-8 h-8 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 inline-flex items-center justify-center text-white font-bold text-base shadow-md shadow-primary-500/25 hover:shadow-primary-500/40 transition-shadow"
                >
                  {logoParts.badge}
                </motion.span>
              </h1>
            </Link>
          </div>

          {/* Right side controls: User name (if logged in) + Burger menu */}
          <div ref={triggerRef} className="flex items-center gap-2 sm:gap-3">

            {currentUser ? (
              <button
                onClick={() => setOpen(prev => !prev)}
                className="group flex items-center gap-2 px-3 py-1.5 rounded-xl bg-dark-200/60 hover:bg-dark-200/90 border border-white/10 hover:border-primary-500/30 transition-all text-sm font-medium"
                title="User menu"
              >
                {currentUser.photo_url ? (
                  <img src={currentUser.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-bold text-xs">
                    {currentUser.first_name ? currentUser.first_name[0].toUpperCase() : <UserIcon className="w-3.5 h-3.5" />}
                  </div>
                )}
                <span className="font-semibold text-white max-w-[120px] sm:max-w-[160px] truncate">
                  {currentUser.first_name || (currentUser.username ? `@${currentUser.username}` : 'Cabinet')}
                </span>
                <svg 
                  className={`w-3.5 h-3.5 text-neutral-400 group-hover:text-white transition-transform duration-200 ${open ? 'rotate-180 text-primary-400' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            ) : (
              authConfig.authEnabled && (
                <Link
                  to="/cabinet?tab=dashboard"
                  className="px-3 py-1.5 rounded-xl text-xs sm:text-sm font-medium text-neutral-300 hover:text-white bg-dark-200/60 hover:bg-dark-200/90 border border-white/10 hover:border-primary-500/30 transition-all flex items-center gap-1.5"
                >
                  <UserCircleIcon className="w-4 h-4 text-primary-400" />
                  <span>Login</span>
                </Link>
              )
            )}

            {/* Burger Menu / Close Button */}
            <motion.button 
              aria-label='Toggle navigation' 
              className={`relative p-2.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                open 
                  ? 'border-white/[0.16] bg-[#090a0f]/90 text-white shadow-[0_4px_20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.12)]' 
                  : 'border-white/[0.12] bg-[#090a0f]/60 hover:bg-[#090a0f]/90 text-neutral-300 hover:text-white hover:border-white/[0.25] shadow-sm'
              }`} 
              onClick={() => setOpen(prevState => !prevState)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-5 h-5 flex flex-col justify-center items-center relative">
                <motion.span 
                  className="block h-0.5 w-5 bg-white transition-all duration-200"
                  animate={{
                    rotate: open ? 45 : 0,
                    y: open ? 3 : 0,
                    opacity: 1
                  }}
                  transition={{ duration: 0.15, ease: "easeInOut" }}
                />
                <motion.span 
                  className="block h-0.5 w-5 bg-white mt-1 transition-all duration-200"
                  animate={{
                    opacity: open ? 0 : 1,
                    x: open ? 8 : 0
                  }}
                  transition={{ duration: 0.15, ease: "easeInOut" }}
                />
                <motion.span 
                  className="block h-0.5 w-5 bg-white mt-1 transition-all duration-200"
                  animate={{
                    rotate: open ? -45 : 0,
                    y: open ? -7 : 0,
                    opacity: 1
                  }}
                  transition={{ duration: 0.15, ease: "easeInOut" }}
                />
              </div>
            </motion.button>
          </div>
        </div>

        {/* Unified Dropdown Menu Card - contained within the max-w-7xl boundary so it aligns perfectly with the burger button */}
        <div className="max-w-7xl mx-auto relative pointer-events-none">
          <div 
            ref={menuRef}
            className={`absolute top-full right-0 mt-5 sm:mt-6 z-50 pointer-events-auto transition-all duration-200 ease-out ${
              open ? 'opacity-100 visible translate-y-0 scale-100' : 'opacity-0 invisible -translate-y-2 scale-95 pointer-events-none'
            }`}
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/[0.14] bg-[#090a0f]/95 backdrop-blur-2xl shadow-[0_25px_70px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.06),inset_0_1px_0_rgba(255,255,255,0.12)] min-w-[270px] sm:min-w-[300px] p-4 sm:p-5">
              {/* Top specular highlight edge line */}
              <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />
              {/* Ambient subtle glow */}
              <div className="absolute inset-0 bg-gradient-to-b from-primary-500/[0.06] via-transparent to-secondary-500/[0.03] pointer-events-none" />

            {/* User Profile Card if logged in - clickable to navigate to dashboard */}
            {currentUser && (
              <div className="relative mb-3">
                <Link
                  to="/cabinet?tab=dashboard"
                  onClick={() => setOpen(false)}
                  className="relative flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-primary-500/30 transition-all cursor-pointer group"
                  title="Go to Personal Cabinet Dashboard"
                >
                  {currentUser.photo_url ? (
                    <img src={currentUser.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-primary-500/30" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-bold text-sm shadow-md group-hover:scale-105 transition-transform">
                      {currentUser.first_name ? currentUser.first_name[0].toUpperCase() : <UserIcon className="w-5 h-5" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm truncate group-hover:text-primary-300 transition-colors">
                      {currentUser.first_name || (currentUser.username ? `@${currentUser.username}` : 'User')} {currentUser.last_name || ''}
                    </p>
                    {currentUser.username && (
                      <p className="text-xs text-neutral-400 truncate">@{currentUser.username}</p>
                    )}
                    {currentUser.telegram_id && (
                      <p className="text-[11px] text-neutral-500 font-mono truncate">ID: {currentUser.telegram_id}</p>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-neutral-400 group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )}

            {/* Navigation Links */}
            <div className={`relative space-y-1 ${currentUser ? 'pt-2.5 border-t border-white/[0.08]' : ''} mb-3.5`}>
              {/* My domains link: hidden if TELEGRAM_USER_AUTH_SECRET is empty and user not logged in */}
              {(authConfig.authEnabled || currentUser) && (
                <Link
                  to="/cabinet?tab=domains"
                  onClick={() => setOpen(false)}
                  className={`group flex items-center justify-between px-3.5 py-2.5 rounded-xl text-white/90 hover:text-white transition-all hover:bg-white/10 border border-transparent hover:border-white/10 text-sm ${
                    location.pathname.startsWith('/cabinet') && location.search.includes('tab=domains') ? 'bg-primary-500/15 border-primary-500/30 text-white font-semibold' : ''
                  }`}
                >
                  <span className="font-medium">My domains</span>
                  <svg className="w-4 h-4 text-neutral-400 group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}

              <Link
                to="/contact.html"
                onClick={() => setOpen(false)}
                className={`group flex items-center justify-between px-3.5 py-2.5 rounded-xl text-white/90 hover:text-white transition-all hover:bg-white/10 border border-transparent hover:border-white/10 text-sm ${
                  location.pathname === '/contact.html' ? 'bg-primary-500/15 border-primary-500/30 text-white font-semibold' : ''
                }`}
              >
                <span className="font-medium">Contact</span>
                <svg className="w-4 h-4 text-neutral-400 group-hover:text-primary-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            {/* Action Buttons: Add Domain for Sale (always in burger menu) and Logout */}
            <div className="relative pt-2.5 border-t border-white/[0.08] space-y-2">
              <button 
                onClick={() => {
                  setOpen(false);
                  setShowContactForm(true);
                }}
                className="w-full relative overflow-hidden rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 p-[1px] transition-all hover:shadow-lg hover:shadow-primary-500/25 active:scale-95 cursor-pointer"
              >
                <div className="relative rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 px-4 py-2.5 transition-all">
                  <span className="flex items-center justify-center gap-2 font-semibold text-white text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Domain for Sale
                  </span>
                </div>
              </button>

              {currentUser && (
                <button
                  onClick={() => {
                    setOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-red-500/25 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 text-xs font-medium transition-all cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Log out</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>

      {/* Backdrop for click outside - rendered outside header to avoid stacking/clipping issues */}
      {open && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-xl animate-in fade-in duration-200"
             onClick={(e) => {
               if (e.target === e.currentTarget) {
                 setShowContactForm(false);
               }
             }}>
          <div className="relative">
            <ContactForm 
              onClose={() => setShowContactForm(false)} 
              isPurchase={false}
              currentUser={currentUser}
              authConfigProp={authConfig}
              onSubmitted={() => {
                window.dispatchEvent(new Event('domainer_domains_updated'));
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}